import { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Table } from "react-bootstrap";

import { useMutation, useQuery } from "@apollo/client";
import { 
  CreateMMilestonesTemplateMutation,
  CreateMMilestonesTemplateMutationVariables,
  DeleteMMilestonesTemplateMutation,
  DeleteMMilestonesTemplateMutationVariables,
  GetAllMarathonMilestoneConfigsQuery,
  GetMarathonEventQuery,
  ItemAvailability,
  LevelRewardInput,
  LevelRewardInputType,
  MarathonMilestoneTemplate,
  UpdateMMilestonesTemplateMutation,
  UpdateMMilestonesTemplateMutationVariables
} from "../../../../../../__gqltypes__";
import EditRewardsModal from "../../../components/EditRewardsModal";
import EditValue from "../../../components/EditValue";
import Chip from "../../../components/Chip";
import { LoadingLogo } from "../../../../devtools/components/Modal";
import { 
  CREATE_MARATHON_MILESTONE_TEMPLATE,
  DELETE_MARATHON_MILESTONE_TEMPLATE,
  GET_ALL_MARATHON_MILESTONE_TEMPLATE,
  UPDATE_MARATHON_MILESTONE_TEMPLATE
} from "../graphql";

function checkIfNumber(val: string): number | undefined {
  return Number.isNaN(+val) ? undefined : +val;
};

type RecursiveOmit<T, K extends PropertyKey> = {
  [P in keyof T as P extends K ? never : P]: T[P] extends Record<string, unknown>
    ? T[P] extends | Date | RegExp | Array<infer V>
      ? T[P]
      : RecursiveOmit<T[P], K>
    : T[P];
};

type MMilestonConfig = GetMarathonEventQuery['liveOps']['getMarathonEvent']['milestones'][0];
export type MMilestone = RecursiveOmit<MMilestonConfig, '__typename'>;
type Template = MarathonMilestoneTemplate;

export function milestoneConfigToRuntime(miles: MMilestonConfig[]): MMilestone[] {
  return miles.map((mile) => {
    return {
      numberOfGuesses: mile.numberOfGuesses,
      basicReward: {
        type: mile.basicReward.type,
        amount: mile.basicReward.amount,
        itemId: mile.basicReward.itemId,
      },
      plusReward: {
        type: mile.plusReward.type,
        amount: mile.plusReward.amount,
        itemId: mile.plusReward.itemId,
      },
    };
  });
}
const MAR_DEF_REWARD: MMilestone['basicReward'] = {
  type: LevelRewardInputType.CURRENCY_COIN,
  amount: 10,
};

type Props = {
  milestones: MMilestone[];
  onUpdateEvent: (milestones: MMilestone[]) => void;
};

export function MarathonMilestonesData({milestones: m_, onUpdateEvent}: Props): JSX.Element {
  const none = 'None';
  const [milestones, setMilestones] = useState<MMilestone[]>(m_);

  const [showEditReward, setShowEditReward] = useState(false);
  const [selectedReward, setSelectedReward] = useState<LevelRewardInput | null>(null);
  const [isPlusEdit, setIsPlusEdit] = useState(false);
  const [isTemplateEdit, setIsTemplateEdit] = useState(false);
  const [rewardIndex, setRewardIndex] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(none);
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateMilestones, setTemplateMilestones] = useState<MMilestone[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [editTemplateId, setEditTemplateId] = useState<string>(none);
  
  const { data: templateData, loading: dataLoading, refetch } = useQuery<GetAllMarathonMilestoneConfigsQuery>(
    GET_ALL_MARATHON_MILESTONE_TEMPLATE
  );

  const [createMMTemplate, { loading: createLoading}] = useMutation<
    CreateMMilestonesTemplateMutation,
    CreateMMilestonesTemplateMutationVariables>(
      CREATE_MARATHON_MILESTONE_TEMPLATE, {
        onCompleted: refetch,
      },
    );
  const [updateMMTemplate, { loading: updateLoading}] = useMutation<
    UpdateMMilestonesTemplateMutation,
    UpdateMMilestonesTemplateMutationVariables>(
      UPDATE_MARATHON_MILESTONE_TEMPLATE, {
        onCompleted: refetch,
      },
    );

  const [deleteMMTemplate, { loading: deleteLoading }] = useMutation<
    DeleteMMilestonesTemplateMutation,
    DeleteMMilestonesTemplateMutationVariables>(DELETE_MARATHON_MILESTONE_TEMPLATE, {
      onCompleted: refetch,
    });

  useEffect(() => {
    setMilestones(m_);
  }, [m_]);

  useEffect(() => {
    if (templateData?.liveOps?.getAllMarathonMilestoneConfigs) {
      setTemplates(templateData.liveOps.getAllMarathonMilestoneConfigs);
    }
  }, [templateData?.liveOps?.getAllMarathonMilestoneConfigs]);

  const handleChangeMilestone = useCallback((val: MMilestone, index: number, isTemplate = false) => {
    const newMilestones = isTemplate ? [...templateMilestones] : [...milestones];
    newMilestones[index] = {
      numberOfGuesses: val.numberOfGuesses,
      basicReward: {
        ...val.basicReward,
      },
      plusReward: {
        ...val.plusReward,
      },
    };
    if (isTemplate) {
      setTemplateMilestones(newMilestones);  
    } else {
      setMilestones(newMilestones);  
    }
  }, [milestones, templateMilestones]);

  const handleShowEditReward = useCallback((idx: number, isEdit: boolean, isPlus: boolean, isTemplate = false) => {
    // setIsEditMode(isEdit);
    const m = isTemplate ? templateMilestones : milestones;
    const rew_ = isPlus ? m[idx].plusReward : m[idx].basicReward;
    setSelectedReward(rew_);
    
    setIsPlusEdit(isPlus);
    setIsTemplateEdit(isTemplate);
    setRewardIndex(idx);
    setShowEditReward(true);
  }, [milestones, templateMilestones]);

  const handleAddMilestone = useCallback((isTemplate = false) => {
    if (isTemplate) {
      setTemplateMilestones((miles) => miles.concat({
      numberOfGuesses: (miles.length + 1) * 10,
      basicReward: MAR_DEF_REWARD,
      plusReward: MAR_DEF_REWARD,
    }));
    } else {
      setMilestones((miles) => miles.concat({
      numberOfGuesses: (miles.length + 1) * 10,
      basicReward: MAR_DEF_REWARD,
      plusReward: MAR_DEF_REWARD,
    }));
    }
  }, []);

  const handleChangeReward = useCallback((newRew: LevelRewardInput, index: number, isPlus: boolean) => {
    const newMilestones = isTemplateEdit ? [...templateMilestones] : [...milestones];
    if (isPlus) {
      newMilestones[index].plusReward = {
        ...newRew,
      };
    } else {
      newMilestones[index].basicReward = {
        ...newRew,
      };
    }
    if (isTemplateEdit) {
      setTemplateMilestones(newMilestones);  
    } else {
      setMilestones(newMilestones);  
    }
    setShowEditReward(false);
  }, [isTemplateEdit, milestones, templateMilestones]);

  const onSelectTemplate = useCallback((id: string, isTemplate = false) => {
    const tmp = templates.find((temp) => temp.id === id);
    if (tmp) {
      const miles = milestoneConfigToRuntime(tmp.milestones);
      if (isTemplate) {
        setEditTemplateId(id);
        setTemplateName(tmp.name);
        setTemplateMilestones(miles);
      } else {
        setSelectedTemplateId(id);
        setMilestones(miles);
        onUpdateEvent(miles);
      }
    } else if (id === none) {
      if (isTemplate) {
        setTemplateName(none);
        setEditTemplateId(none);
        setTemplateMilestones([]);
      } else {
        setSelectedTemplateId(none);
        setMilestones(m_);
      }
    }
  }, [onUpdateEvent, m_, templates]);
  
  const createTemplate = useCallback(() => {
    createMMTemplate({
      variables: {
        input: {
          name: templateName,
          milestones: templateMilestones,
        }
      }
    });
  }, [createMMTemplate, templateName, templateMilestones]);

  const updateTemplate = useCallback(() => {
    updateMMTemplate({
      variables: {
        input: {
          id: editTemplateId,
          name: templateName,
          milestones: templateMilestones,
        }
      }
    });
  }, [editTemplateId, templateName, templateMilestones, updateMMTemplate]);

  const deleteTemplate = useCallback(() => {
    deleteMMTemplate({
      variables: {
        id: editTemplateId,
      }
    });
  }, [deleteMMTemplate, editTemplateId]);

  const templatesLoading = dataLoading || createLoading || updateLoading || deleteLoading;
  return (
  <>
    <EditRewardsModal
        isEdit={true}
        tier={null}
        show={showEditReward}
        level={0}
        reward={selectedReward}
        handleSave={(rew) => handleChangeReward(rew, rewardIndex, isPlusEdit)}
        handleClose={() => setShowEditReward(false)}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        disableSubscriptionDay
        enableItemLevelEdit
        liveOpsAvailabilities={[ItemAvailability.EARN]}
      />
    
    <Card style={{width: '100%', marginBottom: 10}}>
        <Card.Header className="text-center">
          <Card.Title>Milestones</Card.Title>
          <Row>
            <Button className="mr-2" variant='success' onClick={handleAddMilestone}>
              <i className='fas fa-plus'/> Add Milestone
            </Button>
            <Button 
              className="mr-2"
              variant='danger'
              onClick={() => setMilestones([])}>
              <i className='fas fa-trash'/> Clear Milestones
            </Button>
          </Row>
            {/* {!isEnoughRewards && (
              <Card.Text className="text-danger">
              {REQUIRED_NUMBER_OF_REWARDS} rewards are required for Wheel Fortune event
              </Card.Text>
            )} */}
            <Row>
              <Col className="d-flex flex-column align-items-center">
                <Form.Label>Choose Rewards Template</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedTemplateId}
                    className="mb-4 w-25"
                    onChange={(e) => onSelectTemplate(e.target.value)}
                  >
                    <option key={none} value={none}>{none}</option>
                    {templates.map((temp) => {
                      return (
                        <option key={temp.id} value={temp.id}>
                          {temp.name}
                        </option>
                      );
                    })}
                  </Form.Control>
              </Col>
            </Row>
          </Card.Header>
        <Card.Body>
            <Table bordered responsive hover size="sm">
              <thead className="text-center">
                <tr>
                  <th>Basic reward</th>
                  <th>Plus reward</th>
                  <th>Number of guesses</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((val, index) => {
                  return (
                    <tr key={index} >
                      <td className="align-middle" >
                        <div className="d-flex justify-content-center align-items-center">
                          <Chip 
                            reward={val.basicReward} 
                            canDelete={false}
                            globalAction={() => handleShowEditReward(index, true, false)}/>
                        </div>
                      </td>
                      <td className="align-middle" >
                        <div className="d-flex justify-content-center align-items-center">
                          <Chip 
                            reward={val.plusReward} 
                            canDelete={false}
                            globalAction={() => handleShowEditReward(index, true, true)}/>
                        </div>
                      </td>
                      <td >
                        <Col className="d-flex flex-column align-items-center">
                         <EditValue
                            name=""
                            value={val.numberOfGuesses}
                            handleValueChange={(e) => {
                            const newVal = checkIfNumber(e) ?? 0;
                            const m = milestones[index];
                            handleChangeMilestone({...m, numberOfGuesses: newVal }, index);
                          }}/>
                        </Col>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
        </Card.Body>
      </Card>

      <Card style={{width: '100%'}}>
        <Card.Header className="text-center">
          <Card.Title>Templates</Card.Title>
          <Row>
            <Button 
              className="mr-2"
              variant='success'
              onClick={() => handleAddMilestone(true)}
              // disabled={isEnoughTRewards}
              >
              <i className='fas fa-plus'/> Add milestone
            </Button>
            <Button className="mr-2" onClick={createTemplate} 
              // disabled={!isEnoughTRewards}
              >
              <i className='fas fa-plus'/> Create Template
            </Button>
            <Button className="mr-2" onClick={updateTemplate} disabled={editTemplateId === none}>
              <i className='fas fa-edit'/> Update Template
            </Button>
            <Button className="mr-2" variant="danger" onClick={deleteTemplate} disabled={editTemplateId === none}>
              <i className='fas fa-trash'/> Delete Template
            </Button>
            <Button 
              className="mr-2"
              variant='danger'
              onClick={() => setTemplateMilestones([])}>
              <i className='fas fa-trash'/> Clear Rewards
            </Button>
          </Row>
           {/* {!isEnoughTRewards && (
              <Card.Text className="text-danger">{
              REQUIRED_NUMBER_OF_REWARDS} rewards are required for Wheel Fortune event
              </Card.Text>
            )} */}
          <Row>
            <Col className="d-flex flex-column align-items-center">
              <Form.Label className="w-50">Name</Form.Label>
              <Form.Control
                className="w-50"
                type="text"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                }}
                />
            </Col>
            <Col className="d-flex flex-column align-items-center">
            <Form.Label>Choose Rewards Template</Form.Label>
              <Form.Control
                as="select"
                value={editTemplateId}
                className="mb-4 w-50"
                onChange={(e) => onSelectTemplate(e.target.value, true)}
              >
                <option key={none} value={none}>{none}</option>
                {templates.map((temp) => {
                  return (
                    <option key={temp.id} value={temp.id}>
                      {temp.name}
                    </option>
                  );
                })}
              </Form.Control>
              </Col>
          </Row>
        </Card.Header>
         <Card.Body>
          {templatesLoading ? (
            <LoadingLogo show />
          ) : (
            <Table bordered responsive hover size="sm">
              <thead className="text-center">
                <tr>
                  <th>reward</th>
                  <th>weight</th>
                  <th>chance</th>
                </tr>
              </thead>
              <tbody>
                {templateMilestones.map((val, index) => {
                  return (
                    <tr key={index} >
                      <td className="align-middle" >
                        <div className="d-flex justify-content-center align-items-center">
                          <Chip 
                            reward={val.basicReward} 
                            canDelete={false}
                            globalAction={() => handleShowEditReward(index, true, false, true)}/>
                        </div>
                      </td>
                      <td className="align-middle" >
                        <div className="d-flex justify-content-center align-items-center">
                          <Chip 
                            reward={val.plusReward} 
                            canDelete={false}
                            globalAction={() => handleShowEditReward(index, true, true, true)}/>
                        </div>
                      </td>
                      <td >
                        <Col className="d-flex flex-column align-items-center">
                         <EditValue
                            name=""
                            value={val.numberOfGuesses}
                            handleValueChange={(e) => {
                            const newVal = checkIfNumber(e) ?? 0;
                            const m = milestones[index];
                            handleChangeMilestone({...m, numberOfGuesses: newVal }, index);
                          }}/>
                        </Col>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
  </>);
}